import { Fragment, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Dialog, DialogTitle, IconButton, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios } from '../hooks/useAxios'
import { Feature, Package, User, Subscription } from '../types/models'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useApp } from '../hooks/useApp'
import { useVisitUser } from '../hooks/useVisitUser'
import { Add, Delete, Edit } from '@mui/icons-material'
import { SubscriptionInfo } from '../components/SubscriptionInfo'
import { DateTime } from 'luxon'
import { ActivityLog } from '../components/ActivityLog'
import { InfoList, InfoListLines } from '../components/atoms/InfoList'
import { Text } from '../components/atoms/Text'
import { UserGroupsManager } from '../components/UserGroupsManager'

export function AdminUserPage(): React.ReactElement {

  const app = useApp()
  const [user, setUser] = useState<User | null>(null)
  const [features, setFeatures] = useState<Feature[]>([])
  const [packages, setPackages] = useState<Package[]>([])
  const [deletable, setDeletable] = useState(false)
  const [viewable, setViewable] = useState(false)
  const [editing, setEditing] = useState(false)
  const [email, setEmail] = useState('')
  const [verificationSent, setVerificationSent] = useState(false)
  const [packageSelect, setPackageSelect] = useState(false)
  const { t } = useTranslation()
  const { id } = useParams()
  const visitUser = useVisitUser()
  const navigate = useNavigate()

  const onUpdate = () => {
    callAxios<User>({
      url: `/users/${id}`,
    }).then(res => {
      if (res.success) {
        setUser(res.data)
        setEmail(res.data.email)
      } else {
        // E.g. an ADMIN opening a fellow admin: the server answers 404 and the
        // page would otherwise stay silently empty.
        app.addError(res)
      }
    })
  }

  const onSelect = async (p: Package) => {
    const time = DateTime.local().plus({ days: 90 })
    const result = await callAxios<Subscription>({
      url: '/subscriptions',
      method: 'POST',
    }, { user_id: user?.id, package_id: p.id, expires: time.toISO() })

    if (result.success) {
      onUpdate && onUpdate()
    } else {
      app.addError(result)
    }

    setPackageSelect(false)
  }

  useEffect(() => {
    onUpdate()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    callAxios<Feature[]>({
      url: '/features',
    }).then(res => res.success && setFeatures(res.data))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    callAxios<Package[]>({
      url: '/packages',
    }).then(res => res.success && setPackages(res.data))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDelete = () => {
    setDeletable(false)
    // Never send DELETE /users/null: the user may not have loaded (404) at all.
    if (!user?.id) {
      return
    }
    callAxios({
      method: 'DELETE',
      url: `/users/${user.id}`
    }).then((res) => {
      if (res.success) {
        navigate('/admin/users')
      } else {
        app.addError(res)
      }
    })
  }

  const onView = () => {
    if (!viewable || !user) {
      return
    }
    visitUser(user.id!).then((success) => {
      if (!success) {
        setViewable(false)
      }
    })
  }

  const onResend = () => {
    if (!user?.id) {
      return
    }
    callAxios({
      method: 'PUT',
      url: `/users/${user.id}/resend-verification`
    }).then((res) => {
      if (res.success) {
        app.addInfo(t('Verification link sent.'))
        setVerificationSent(true)
      } else {
        app.addError(res)
      }
    })
  }

  const onChangeEmail = (email: string) => {
    if (!user?.id) {
      return
    }
    callAxios({
      method: 'PATCH',
      url: `/users/${user.id}`,
    }, { email }).then(async res => {
      if (res.success) {
        await onUpdate()
        app.addInfo(t('Email changed.'))
      } else {
        app.addError(res)
      }
    })

  }

  // Find out what user does not have.
  const missingFeatures = new Set(features)
  const notSubscribed = new Set(packages)

  user && user.subscriptions && user.subscriptions.forEach(s => {
    if (!s.expired) {
      s.package.features.forEach(f => {
        missingFeatures.delete(f)
      })
    }
    const pck = packages.find(p => p.id === s.package.id)
    if (pck) {
      notSubscribed.delete(pck)
    }
  })

  if (user && user.role === 'ROOT') {
    missingFeatures.clear()
  }

  const detailLines: InfoListLines<User> = {
    [t('Column-Last Login')]: (u: User) => u.last_login
      ? DateTime.fromISO(u.last_login).toLocaleString(DateTime.DATETIME_SHORT)
      : '',
    ...(app.user?.role === 'ROOT'
      ? {
          [t('Column-Last IP')]: 'last_ip',
          [t('Who Created the User')]: 'created_by',
        }
      : {}),
  }

  return (
    <Fragment>
      {
        !editing && <PageTitle title={user ? <>{user.email} <IconButton onClick={() => setEditing(true)}><Edit/></IconButton></> : ''}/>
      }
      {
        editing && <Text autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === 'Escape') {
                          setEmail(user ? user.email : '')
                          setEditing(false)
                        }
                        if (e.key === 'Enter') {
                          setEditing(false)
                          onChangeEmail(email)
                        }
                      }}
                    />
      }

      <PageSubtitle title={t('Details')}/>
      <InfoList lines={detailLines} item={user}/>

      {
        app.user?.role === 'ROOT' &&
        <>
          <PageSubtitle title={t('Subscriptions')}/>
          <Box sx={{ mt: 2 }}>
            {user && user.subscriptions && user.subscriptions.map(
              s => <SubscriptionInfo key={s.id} sx={{ mt: 3 }} subscription={s} onUpdate={onUpdate}/>
            )}
          </Box>
        </>
      }

      { missingFeatures.size > 0 &&
        <Box sx={{ mt: 4 }} data-cy="box-Features not available">
          <Typography sx={{ mb: 1 }} color="secondary.main">
            <Trans>Features not available</Trans>
            <IconButton onClick={() => setPackageSelect(true)}><Add/></IconButton>
          </Typography>
          {[...missingFeatures].map(f => <Box key={f} sx={{ ml: 3, mt: 1 }}>✴<> </>{t(`Feature-${f}`)}</Box>)}
        </Box>
      }
      <Button data-cy="button-Log in as User" sx={{ mt: 3, mb: 2 }} fullWidth onClick={() => setViewable(true)}>
          <Trans>Log in as User</Trans>
      </Button>
      <Button sx={{ mb: 2, backgroundColor: 'success.main' }} disabled={verificationSent || !user || user.is_verified} fullWidth onClick={onResend}>
          <Trans>Resend Verification Link</Trans>
      </Button>

      {user && app.user?.role === 'ROOT' && <UserGroupsManager user={user}/>}

      <Box data-cy="box-Activity Log">
        <PageSubtitle title={t('Activity Log')}/>
        {user && <ActivityLog user={user}/>}
      </Box>

      {user &&
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <IconButton data-cy="button-Delete User" sx={{ color: 'error.dark' }} onClick={() => setDeletable(true)}><Delete/></IconButton>
        </Box>
      }

      {viewable &&
        <Dialog open={!!viewable} onClose={() => setViewable(false)}>
            <DialogTitle><Trans>View User?</Trans></DialogTitle>
            <Box sx={{ p: 2 }}>
              <Typography variant="h2">Email: {user && user.email}</Typography>
              <Trans>Are you sure that you want to log in as a user?</Trans>
              <Stack direction="row" spacing={2} sx={{ m: 2 }}>
                <Button color="primary" onClick={() => setViewable(false)}><Trans>Cancel</Trans></Button>
                <Button data-cy="button-Log in as User OK" color="secondary" onClick={onView}><Trans>OK</Trans></Button>
              </Stack>
            </Box>
        </Dialog>
      }

      {deletable && user &&
        <Dialog open={!!deletable} onClose={() => setDeletable(false)}>
            <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Delete User?</Trans></DialogTitle>
            <Box sx={{ p: 2 }}>
              <Typography variant="h2">Email: {user && user.email}</Typography>
              <Trans>Are you sure that you want to delete user?</Trans>
              <Stack direction="row" spacing={2} sx={{ m: 2 }}>
                <Button color="primary" onClick={() => setDeletable(false)}><Trans>Cancel</Trans></Button>
                <Button data-cy="button-Delete User OK" color="secondary" onClick={onDelete}><Trans>OK</Trans></Button>
              </Stack>
            </Box>
        </Dialog>
      }

      {packageSelect &&
        <Dialog open={!!packageSelect} onClose={() => setPackageSelect(false)}>
            <DialogTitle><Trans>Select Package</Trans></DialogTitle>
            <Box>
              <List>
                {[...notSubscribed].map(p => <ListItemButton key={p.id} onClick={() => onSelect(p)}>
                  <ListItem>{t(`Package-${p.code}`)}</ListItem>
                </ListItemButton>)}
              </List>
              <Stack spacing={2} sx={{ m: 2 }}>
                <Button color="primary" onClick={() => setPackageSelect(false)}><Trans>Cancel</Trans></Button>
              </Stack>
            </Box>
        </Dialog>
      }

    </Fragment>
  )
}
